import { CollectionQueryParams, ID } from './common';
import { Permission } from './permissions';
import { FullSource } from './source';
import { FullPrompt, PromptAttachment, PromptType } from './prompt';

export enum AlertRuleActionType {
    APP_NOTIFICATION = 'Notification',
    WHATSAPP = 'WhatsApp',
    EMAIL = 'Email',
    WEBHOOK = 'Webhook',
}

export interface Recipient {
    id: ID;
    createdAt: number;
    updatedAt: number;
    email?: string;
    phone?: string;
}

export interface WebhookHeader {
    id: string;
    name: string;
    value: string;
}

export enum WebhookAuthorizationType {
    BEARER = 'bearer',
    BASIC = 'basic',
}

export interface WebhookAuthorizationPayload {
    type: WebhookAuthorizationType;
    username?: string;
    secret?: string;
}

export interface WebhookStoredCredential {
    type: WebhookAuthorizationType;
    origin: string;
}

export interface WebhookAuthorizationForm {
    type: WebhookAuthorizationType | null;
    username: string;
    secret: string;
    stored: WebhookStoredCredential | null;
}

export interface Webhook {
    id: ID;
    createdAt: number;
    updatedAt: number;
    url: string;
    headers: Array<{ name: string; value: string }>;
    authorization?: {
        type: WebhookAuthorizationType;
        username?: string;
    } | null;
}

export interface WebhookTestRequest {
    url: string;
    headers: Array<{ name: string; value: string }>;
    authorization?: WebhookAuthorizationPayload | null;
    alertRuleActionId?: string;
}

export interface WebhookTestResult {
    requestId: string;
    statusCode?: number;
    latencyMs: number;
    error?: string;
    responseSnippet?: string;
}

export const WebhookDeliveryStatus = {
    SUCCEEDED: 'succeeded',
    ERRORED: 'errored',
    NO_RESPONSE: 'no_response',
} as const;

export type WebhookDeliveryStatus =
    (typeof WebhookDeliveryStatus)[keyof typeof WebhookDeliveryStatus];

export type WebhookDeliveryChartPoint = {
    date: string;
    startTime: number;
    endTime: number;
    succeeded: number;
    errored: number;
    noResponse: number;
    p95Succeeded?: number | null;
    p95Errored?: number | null;
    p95NoResponse?: number | null;
};

export interface WebhookDeliveryStats {
    chartData: WebhookDeliveryChartPoint[];
    total: number;
    succeeded: number;
    errored: number;
    noResponse: number;
    p95LatencyMs?: number | null;
    p95SucceededMs?: number | null;
    p95ErroredMs?: number | null;
    p95NoResponseMs?: number | null;
    lastSuccessAt?: number | null;
}

export interface WebhookDelivery {
    id: ID;
    createdAt: number;
    updatedAt: number;
    webhookId: ID;
    eventId: ID;
    url: string;
    requestId: string;
    statusCode?: number | null;
    latencyMs?: number | null;
    error?: string | null;
    responseSnippet?: string | null;
}

export interface WebhookDeliveryDetail extends WebhookDelivery {
    requestBody: string;
}

export interface AlertRule {
    id: ID;
    createdAt: number;
    updatedAt: number;
    name: string;
    userId: ID;
    active: boolean;
}

export interface AlertRuleAction {
    id: ID;
    createdAt: number;
    updatedAt: number;
    alertRuleId: ID;
    type: AlertRuleActionType;
    threshold: number;
    recipient?: Pick<Recipient, 'id' | 'email' | 'phone'>;
    webhook?: Pick<Webhook, 'id' | 'url' | 'headers' | 'authorization'>;
}

export interface LastAlertInfo {
    id: string;
    createdAt: number;
}

export interface FullAlertRule extends AlertRule {
    actions: Array<
        Omit<AlertRuleAction, 'alertRuleId' | 'createdAt' | 'updatedAt'>
    >;
    sources: Array<Pick<FullSource, 'id' | 'name' | 'src' | 'type'>>;
    prompts: Array<
        Pick<FullPrompt, 'id' | 'text' | 'type'> & {
            attachments: Array<Pick<PromptAttachment, 'id' | 'type' | 'url'>>;
        }
    >;
    lastAlert?: LastAlertInfo;
}

export type AlertRulePrompt = FullAlertRule['prompts'][number];
export type AlertRuleActionData = FullAlertRule['actions'][number];

export type AlertRuleQueryParams = CollectionQueryParams & {
    active?: boolean;
    sourceId?: string;
};

export type AlertRuleEventsQueryParams = CollectionQueryParams & {
    alertRuleId?: string[];
    sourceId?: string[];
    folderId?: string[];
    minConfidence?: number;
    maxConfidence?: number;
};

export type NewAlertRule = Omit<
    FullAlertRule,
    'userId' | 'createdAt' | 'updatedAt' | 'sources' | 'prompts' | 'actions'
> & {
    sources: string[];
    prompts: Array<{
        id?: string;
        text: string;
        type: PromptType;
        attachments: Array<{
            id?: string;
            type: string;
            url: string;
        }>;
    }>;
    actions: Array<{
        id?: string;
        type: AlertRuleActionType;
        threshold: number;
        recipient?: {
            id?: string;
            phone?: string;
        };
        webhook?: {
            url: string;
            headers: Array<{ name: string; value: string }>;
            authorization?: WebhookAuthorizationPayload | null;
        };
    }>;
};

export enum AlertRuleCreationStep {
    SELECT_SOURCES = 'select-sources',
    DEFINE_PROMPT = 'define-prompt',
    CONFIGURE_ACTIONS = 'choose-alert-method',
}

export enum AlertRuleCreationFormField {
    NAME = 'name',
    SOURCES = 'sources',
    PROMPT = 'prompt',
    ACTIONS = 'actions',
    ACTIVE = 'active',
}

export interface ActionConfig {
    id?: string;
    type: AlertRuleActionType;
    confidenceThreshold: number;
    recipient?: {
        phone?: string;
    };
    recipients?: Array<{
        id: string;
        phone: string;
        confidenceThreshold: number;
    }>;
    webhook?: {
        url: string;
        headers: WebhookHeader[];
        authorization: WebhookAuthorizationForm;
    };
}

export interface AlertFormData {
    name?: string;
    sources: string[];
    prompt: string;
    attachment?: { url: string; type?: string };
    actions: ActionConfig[];
    active: boolean;
}

export interface AlertActionItem {
    title: string;
    onClick: () => void;
    shortcut?: string[];
    permission?: Permission;
}

export interface AlertActionCardConfig {
    type: AlertRuleActionType;
    icon: React.ComponentType<{ size?: number; className?: string }>;
    title: string;
    subtitle: string;
    enabled: boolean;
}

export interface AlertRecipient {
    id: string;
    phone: string;
    confidenceThreshold: number;
}

export interface AlertRuleStatsSourceData {
    id: string;
    name: string;
    type: string;
    value: number;
    max: number;
}

export interface AlertRuleStats {
    totalEvents: number;
    chartData: Array<{
        date: string;
        value: number;
        startTime: number;
        endTime: number;
        sourceCounts: Array<{ id: string; value: number }>;
    }>;
    sourceStats: AlertRuleStatsSourceData[];
}
